// =============================================
// Push Notification Service
// רישום והרשמה ל-Web Push Notifications
// =============================================

import { API_BASE_URL } from './api';
import { useAuthStore } from '../stores/authStore';

// ==========================================
// Helpers
// ==========================================

/** המרת VAPID public key מ-Base64URL ל-Uint8Array */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** קבלת Authorization header */
function getAuthHeader(): Record<string, string> {
  const token = useAuthStore.getState().token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ==========================================
// Public API
// ==========================================

/** בדיקה האם Push notifications נתמכים בדפדפן */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** בדיקה האם יש הרשאה לNotifications */
export function getNotificationPermission(): NotificationPermission {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
}

/** בקשת הרשאה מהמשתמש */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) return 'denied';
  return await Notification.requestPermission();
}

/** רישום Service Worker והרשמה ל-Push */
export async function subscribeToPush(): Promise<boolean> {
  if (!isPushSupported()) {
    console.warn('Push notifications not supported');
    return false;
  }

  try {
    // 1. בקשת הרשאה
    const permission = await requestNotificationPermission();
    if (permission !== 'granted') {
      console.log('Push permission denied');
      return false;
    }

    // 2. רישום Service Worker
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;

    // 3. קבלת VAPID Public Key מהשרת
    const vapidResponse = await fetch(`${API_BASE_URL}/push/vapid-key`);
    const vapidData = await vapidResponse.json();
    const vapidPublicKey = vapidData?.data?.publicKey;

    if (!vapidPublicKey) {
      console.error('Failed to get VAPID public key');
      return false;
    }

    // 4. יצירת Push subscription
    const vapidKeyArray = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyArray.buffer as ArrayBuffer,
    });

    // 5. שליחת ה-subscription לשרת
    const subJson = subscription.toJSON();
    const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        endpoint: subJson.endpoint,
        p256dh: subJson.keys?.p256dh || '',
        auth: subJson.keys?.auth || '',
      }),
    });

    if (response.ok) {
      console.log('Push subscription saved successfully');
      return true;
    } else {
      console.error('Failed to save push subscription:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Error subscribing to push:', error);
    return false;
  }
}

/** ביטול הרשמה ל-Push */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // ביטול ב-browser
      await subscription.unsubscribe();

      // הודעה לשרת
      await fetch(`${API_BASE_URL}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
      });
    }

    return true;
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    return false;
  }
}

/** בדיקה האם כבר רשום ל-Push */
export async function isSubscribedToPush(): Promise<boolean> {
  if (!isPushSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}

/**
 * אתחול Push Notifications — קוראים ב-login/startup
 * רק אם המשתמש כבר נתן הרשאה (לא מבקשים הרשאה אוטומטית)
 */
export async function initPushNotifications(): Promise<void> {
  if (!isPushSupported()) return;

  // רק אם ההרשאה כבר ניתנה
  if (Notification.permission === 'granted') {
    const isSubscribed = await isSubscribedToPush();
    if (!isSubscribed) {
      await subscribeToPush();
    }
  }
}
